import React, { useMemo, useState } from 'react';
import { StanTable, StanTag } from '@/components';
import type { StanColumnDef } from '@/components/core-components/StanTable/StanTable.types';
import type { MFAConfig } from '@/lib/api/settings/types';
import { formatDistanceToNow } from 'date-fns';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import type { DropdownMenuOption } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { LuEllipsisVertical } from 'react-icons/lu';
import IcEdit from '@/assets/icons/ic-edit-01.svg';
import IcTrash from '@/assets/icons/ic-trash.svg';
import { MFA_STATUS } from '../../constants/mfa';

interface MFAMethodsTableProps {
    configs: MFAConfig[];
    isLoading: boolean;
    onRename: (config: MFAConfig) => void;
    onDelete: (config: MFAConfig) => void;
}

const ITEMS_PER_PAGE = 2;

const MFA_METHOD_OPTIONS: DropdownMenuOption[] = [
    {
        label: 'Rename',
        value: 'rename',
        icon: IcEdit,
    },
    {
        label: 'Delete',
        value: 'delete',
        icon: IcTrash,
        type: 'destructive',
    },
];

const handleMFAOptionSelect = (
    value: string,
    config: MFAConfig,
    onRename: (config: MFAConfig) => void,
    onDelete: (config: MFAConfig) => void
) => {
    if (value === 'rename') {
        onRename(config);
    } else if (value === 'delete') {
        onDelete(config);
    }
};

const createMFAColumns = (
    onRename: (config: MFAConfig) => void,
    onDelete: (config: MFAConfig) => void
): StanColumnDef<MFAConfig>[] => [
    {
        accessorKey: 'name',
        header: 'Method',
        cell: ({ row }) => (
            <div className='flex items-center gap-3'>
                <div className='flex flex-col'>
                    <div className='flex items-center gap-2'>
                        <span className='text-body-md font-bold'>{row.original.name || 'Unknown'}</span>
                        {row.original.status === MFA_STATUS.ACTIVE && (
                            <StanTag label='Default' className='bg-brand-subtle text-brand-main font-bold' />
                        )}
                    </div>
                    <span className='text-caption-2-regular'>{row.original.method || 'Authenticator app'}</span>
                </div>
            </div>
        ),
    },
    {
        accessorKey: 'created_at',
        header: 'Date added',
        cell: ({ row }) => (
            <div className='text-caption-1-regular'>
                {formatDistanceToNow(new Date(row.original.created_at * 1000), { addSuffix: true })}
            </div>
        ),
    },
    {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
            <div className='flex items-center justify-end'>
                <DropdownProvider dropdownId={`mfa-menu-${row.original.id}`}>
                    <StanDropdownMenu
                        id={`mfa-menu-${row.original.id}`}
                        name={`mfa-menu-${row.original.id}`}
                        options={MFA_METHOD_OPTIONS}
                        onSelect={value => handleMFAOptionSelect(value.value, row.original, onRename, onDelete)}
                        placement='bottom-end'
                        minWidth={140}
                        rounded
                    >
                        <button className='text-gray hover:text-dark flex size-8 cursor-pointer items-center justify-center rounded-md transition hover:bg-neutral-100'>
                            <LuEllipsisVertical className='size-5' />
                        </button>
                    </StanDropdownMenu>
                </DropdownProvider>
            </div>
        ),
        align: 'right',
    },
];

// Mobile columns - simplified for smaller screens
const createMobileMFAColumns = (
    onRename: (config: MFAConfig) => void,
    onDelete: (config: MFAConfig) => void
): StanColumnDef<MFAConfig>[] => [
    {
        accessorKey: 'name',
        header: 'Method',
        cell: ({ row }) => (
            <div className='flex flex-col gap-1 py-1'>
                <div className='flex flex-wrap items-center gap-2'>
                    <span className='text-body-md max-w-52 truncate font-bold'>{row.original.name || 'Unknown'}</span>
                    {row.original.status === MFA_STATUS.ACTIVE && (
                        <StanTag label='Default' className='bg-brand-subtle text-brand-main font-bold' />
                    )}
                </div>
                <span className='text-gray text-caption-2-regular'>{row.original.method || 'Authenticator app'}</span>
            </div>
        ),
    },
    {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
            <div className='flex items-center justify-end'>
                <DropdownProvider dropdownId={`mfa-menu-mobile-${row.original.id}`}>
                    <StanDropdownMenu
                        id={`mfa-menu-mobile-${row.original.id}`}
                        name={`mfa-menu-mobile-${row.original.id}`}
                        options={MFA_METHOD_OPTIONS}
                        onSelect={value => handleMFAOptionSelect(value.value, row.original, onRename, onDelete)}
                        placement='bottom-end'
                        minWidth={140}
                        rounded
                    >
                        <button className='text-gray hover:text-dark flex size-8 cursor-pointer items-center justify-center rounded-md transition hover:bg-neutral-100'>
                            <LuEllipsisVertical className='size-5' />
                        </button>
                    </StanDropdownMenu>
                </DropdownProvider>
            </div>
        ),
        align: 'right',
    },
];

export const MFAMethodsTable: React.FC<MFAMethodsTableProps> = ({ configs, isLoading, onRename, onDelete }) => {
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = useMemo(() => Math.ceil(configs.length / ITEMS_PER_PAGE), [configs.length]);

    const paginatedConfigs = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return configs.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [configs, currentPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const desktopColumns = useMemo(() => createMFAColumns(onRename, onDelete), [onRename, onDelete]);

    const mobileColumns = useMemo(() => createMobileMFAColumns(onRename, onDelete), [onRename, onDelete]);

    if (configs.length === 0 && !isLoading) {
        return null;
    }

    return (
        <div className='mt-4'>
            {/* Desktop Table */}
            <div className='hidden lg:block'>
                <StanTable<MFAConfig>
                    data={paginatedConfigs}
                    columns={desktopColumns}
                    isLoading={isLoading}
                    page={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>

            {/* Mobile Table */}
            <div className='lg:hidden'>
                <StanTable<MFAConfig>
                    data={paginatedConfigs}
                    columns={mobileColumns}
                    isLoading={isLoading}
                    page={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            </div>
        </div>
    );
};
